import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Grow,
  Fade,
  Button
} from '@mui/material';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import EsdBandImage from '../assets/esd_band.png';
import EsdShoesImage from '../assets/esd_shoes.png';
import EsdSignImage from '../assets/esd_sign.png';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    gap: '20px',
    minHeight: '80vh',
  },
  searchContainer: {
    width: '100%',
    maxWidth: '600px',
    marginTop: '10px',
  },
  searchField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.23)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.4)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#90caf9',
      },
    },
    '& .MuiInputBase-input': {
      color: '#fff',
    },
  },
  optionsGrid: {
    width: '100%',
    maxWidth: '900px',
    marginTop: '20px',
  },
  optionCard: {
    backgroundColor: '#303031',
    height: '200px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)',
      backgroundColor: '#404042',
    },
  },
  icon: {
    fontSize: '64px',
    marginBottom: '16px',
    color: '#90caf9',
  },
  cardIcon: {
    width: '64px',
    height: '64px',
    marginBottom: '16px',
    filter: 'brightness(0.9)',
  },
  cardContent: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  cardTitle: {
    color: '#fff',
    fontSize: '1.25rem',
    fontWeight: 500,
  },
  resultMessage: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 9999,
    padding: '20px 40px',
    borderRadius: '10px',
    fontSize: '2rem',
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: '#303031',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
  },
  success: {
    color: '#4caf50',
  },
  error: {
    color: '#f44336',
  },
  statusDialog: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#303031',
    padding: '40px',
    borderRadius: '8px',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
    minWidth: '500px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  statusQuestion: {
    color: '#fff',
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  statusButtons: {
    display: 'flex',
    gap: '30px',
    justifyContent: 'center',
  },
  passButton: {
    backgroundColor: '#303031',
    color: '#4caf50',
    padding: '20px 60px',
    fontSize: '1.5rem',
    borderRadius: '8px',
    border: '2px solid #4caf50',
    '&:hover': {
      backgroundColor: 'rgba(76, 175, 80, 0.2)',
      transform: 'scale(1.05)',
    },
  },
  failButton: {
    backgroundColor: '#303031',
    color: '#f44336',
    padding: '20px 60px',
    fontSize: '1.5rem',
    borderRadius: '8px',
    border: '2px solid #f44336',
    '&:hover': {
      backgroundColor: 'rgba(244, 67, 54, 0.2)',
      transform: 'scale(1.05)',
    },
  },
};

const EsdCheck = () => {
  // State declarations
  const [searchTerm, setSearchTerm] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [userData, setUserData] = useState(null);
  const [verificationToken, setVerificationToken] = useState(null);
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [showStatusSelection, setShowStatusSelection] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultSuccess, setResultSuccess] = useState(false);
  const containerRef = useRef(null);
  const textFieldRef = useRef(null);

  // Update UUID validation to handle standard UUID format
  const isValidUuid = (uuid) => {
    // Standard UUID format (8-4-4-4-12) or simpler alphanumeric format
    const standardUuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const simpleUuidRegex = /^[a-zA-Z0-9]{8,36}$/;
    return standardUuidRegex.test(uuid) || simpleUuidRegex.test(uuid);
  };

  // Replace '+' or '/' from mis-configured scanners with '-'
  const normalizeUuid = (val) => val?.replace(/[+/]/g, '-');

  // Sanitize input to prevent XSS attacks, but preserve dashes for UUIDs
  const sanitizeInput = (input) => {
    if (!input) return '';
    // Remove potentially dangerous characters but keep dashes for UUIDs
    return input.replace(/[<>()&;='"]|[^a-zA-Z0-9-]/g, '');
  };

  const verifyUuid = async (uuid) => {
    // Normaliser først
    const normalizedUuid = normalizeUuid(uuid);

    // Sanitize and validate UUID input
    const sanitizedUuid = sanitizeInput(normalizedUuid);
    
    if (!isValidUuid(sanitizedUuid)) {
      setError('Invalid ID format');
      setShowOptions(false);
      setUserData(null);
      setVerificationToken(null);
      return;
    }
    
    try {
      // Add timeout for API calls to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/esd/verify-uuid/${sanitizedUuid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        
        // Validate response data
        if (!data || !data.token) {
          setError('Invalid response from server');
          setShowOptions(false);
          setUserData(null);
          setVerificationToken(null);
          return;
        }
        
        setUserData(data);
        setVerificationToken(data.token);
        setShowOptions(true);
        setError('');
      } else {
        if (response.status === 429) {
          setError('Too many requests. Please try again later.');
        } else {
          setError('Invalid ID');
        }
        setShowOptions(false);
        setUserData(null);
        setVerificationToken(null);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        console.error('Error verifying UUID:', error);
        setError('System error');
      }
      setShowOptions(false);
      setUserData(null);
      setVerificationToken(null);
    }
  };

  // Add rate limiting for input changes
  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    // Prevent extremely long inputs
    if (value.length > 50) {
      setError('Input too long');
      return;
    }
    setSearchTerm(value);
    setError('');
    // Fjernet: unngå markering mens brukeren skriver
  }, []);

  // Trigger verifyUuid on Enter/Tab
  const handleKeyDown = useCallback((event) => {
    if ((event.key === 'Enter' || event.key === 'Tab') && searchTerm.length >= 8) {
      verifyUuid(searchTerm);
      // Marker teksten når verifisering er trigget
      textFieldRef.current?.select();
    }
  }, [searchTerm]);

  // Fjerner debounce: verifyUuid kalles kun på Enter/Tab
  useEffect(() => {
    if (searchTerm.length < 8) {
      setShowOptions(false);
      setUserData(null);
      setVerificationToken(null);
    }
  }, [searchTerm]);

  // Secure the status selection handler with validation
  const handleStatusSelect = async (status) => {
    if (!verificationToken || !selectedType) return;

    // Validate type input
    const validTypes = ['wristband', 'shoes', 'both'];
    if (!validTypes.includes(selectedType)) {
      setError('Invalid measurement type');
      setResultSuccess(false);
      setShowResult(true);
      setTimeout(() => {
        setShowResult(false);
      }, 2000);
      return;
    }

    try {
      // Add timeout for API calls
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/esd/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          measurement_type: selectedType,
          status: status ? true : false, // Ensure boolean value
          token: verificationToken
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        setResultSuccess(true);
        setShowResult(true);
        setTimeout(() => {
          setShowResult(false);
          setSearchTerm('');
          setShowOptions(false);
          setUserData(null);
          setVerificationToken(null);
          setSelectedType(null);
          setShowStatusSelection(false);
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to log ESD check');
        setResultSuccess(false);
        setShowResult(true);
        setTimeout(() => {
          setShowResult(false);
          setSelectedType(null);
          setShowStatusSelection(false);
        }, 2000);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        console.error('Error logging ESD check:', error);
        setError('System error');
      }
      setResultSuccess(false);
      setShowResult(true);
      setTimeout(() => {
        setShowResult(false);
        setSelectedType(null);
        setShowStatusSelection(false);
      }, 2000);
    }
  };

  const options = [
    { 
        type: 'wristband', 
        title: 'ESD Wristband', 
        icon: (
          <img 
            src={EsdBandImage} 
            alt="ESD Band" 
            style={{ width: '64px', height: '64px', marginBottom: '16px' }}
          />
        )
    },
    { 
        type: 'shoes', 
        title: 'ESD Shoes', 
        icon: (
          <img 
            src={EsdShoesImage} 
            alt="ESD Shoes" 
            style={{ width: '64px', height: '64px', marginBottom: '16px' }}
          />
        )
    },
    { type: 'both', title: 'Both', icon: <FlashOnIcon sx={styles.icon} /> }
  ];

  const getTestTypeLabel = (type) => {
    switch(type) {
      case 'wristband': return 'Wristband';
      case 'shoes': return 'Shoes';
      case 'both': return 'Wristband & Shoes';
      default: return type;
    }
  };

  // Add focus and selection on container click
  const handleContainerClick = () => {
    textFieldRef.current?.focus();
    textFieldRef.current?.select();
  };

  // Handle option selection for ESD check type
  const handleOptionSelect = (type) => {
    setSelectedType(type);
    setShowStatusSelection(true);
  };

  // Set focus on input field when component mounts
  useEffect(() => {
    const handleFocus = () => {
      textFieldRef.current?.select();
    };

    // Select text on focus
    textFieldRef.current?.addEventListener('focus', handleFocus);

    // Ensure initial focus and selection
    textFieldRef.current?.focus();
    textFieldRef.current?.select();

    return () => {
      textFieldRef.current?.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Set focus when clicking anywhere on the page
  useEffect(() => {
    const handlePageClick = () => {
      textFieldRef.current?.focus();
      textFieldRef.current?.select();
    };
    document.addEventListener('click', handlePageClick);
    return () => document.removeEventListener('click', handlePageClick);
  }, []);

  // Set public page flag
  useEffect(() => {
    sessionStorage.setItem('onEsdCheckPage', 'true');
    return () => {
      sessionStorage.removeItem('onEsdCheckPage');
    };
  }, []);

  return (
    <Box sx={styles.container} ref={containerRef} onClick={handleContainerClick}>
      <Box sx={{ mb: 2, mt: 4, display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '40px' }}>
        <Box
          component="img"
          src={EsdSignImage}
          alt="ESD Sign"
          sx={{ width: '290px', height: 'auto', display: 'block' }}
        />
        <Box
          component="img"
          src={require('../assets/Elcare_Nordic_white.png')}
          alt="Elcare Nordic Logo"
          sx={{ width: '340px', height: 'auto', display: 'block' }}
        />
      </Box>
      <Box sx={styles.searchContainer}>
        <TextField
          sx={styles.searchField}
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          placeholder="Scan your ID..."
          inputRef={textFieldRef}
          autoFocus
          error={!!error}
        />
      </Box>

      {error && (
        <Typography variant="body1" color="error" gutterBottom>
          {error}
        </Typography>
      )}
      
      {userData && (
        <Typography variant="h5" component="h2" gutterBottom >
          Welcome, {userData.fullName}, ready to register ESD result
        </Typography>
      )}

      {showOptions && (
        <Grow in={true} timeout={500}>
          <Grid container spacing={3} sx={styles.optionsGrid}>
            {options.map((option) => (
              <Grid item xs={12} sm={4} key={option.type}>
                <Card>
                  <CardActionArea 
                    sx={styles.optionCard}
                    onClick={() => handleOptionSelect(option.type)}
                  >
                    <CardContent sx={styles.cardContent}>
                      {option.type === 'both' ? (
                        <FlashOnIcon sx={styles.icon} />
                      ) : (
                        <Box
                          component="img"
                          src={option.type === 'wristband' ? EsdBandImage : EsdShoesImage}
                          alt={option.title}
                          sx={styles.cardIcon}
                        />
                      )}
                      <Typography sx={styles.cardTitle}>
                        {option.title}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grow>
      )}

      {showResult && (
        <Fade in={showResult}>
          <Box sx={{
            ...styles.resultMessage,
            ...(resultSuccess ? styles.success : styles.error)
          }}>
            {resultSuccess ? 'Successfully registered' : 'Failed registering result'}
          </Box>
        </Fade>
      )}

      {showStatusSelection && (
        <Box sx={styles.statusDialog}>
          <Typography sx={styles.statusQuestion}>
            Did the ESD test pass or fail?
          </Typography>
          <Box sx={styles.statusButtons}>
            <Button
              sx={styles.passButton}
              onClick={() => handleStatusSelect(true)}
            >
              PASS
            </Button>
            <Button
              sx={styles.failButton}
              onClick={() => handleStatusSelect(false)}
            >
              FAIL
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EsdCheck;
